import React, { useCallback } from 'react';
import 'react-dropzone-uploader/dist/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import * as searchConstants from '../../Constants/Search';
import * as searchActions from '../../redux/actions/searchActions';
import * as assetsActions from '../../redux/actions/assetsAction';
import { debounce } from 'throttle-debounce';
import { Box, CircularProgress, Grid, TextField, Tooltip, Typography } from '@material-ui/core';
import CheckCircleSharpIcon from '@material-ui/icons/CheckCircleSharp';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import { apiCallDebounceTime } from '../../Constants/Api';
import ManageAssetInNewTabLink from '../ManageAsset/ManageAssetInNewTabLink';
import ArchiveIcon from '@material-ui/icons/Archive';

//constants
const checkingForAsstTxt = 'Checking existing assets…';
const preAssetExistMsg = 'Asset exist.';
const assetExistAndArchived = `Archived ${preAssetExistMsg}`;
const postAssetExistMsg = ' to go edit this asset.';
const clickHereTxt = 'Click here';
const assetNameTxtBoxTooltip = 'Click to enter a name for this asset. Asset names are checked for duplicates.';

//styles
const useStyles = makeStyles((theme) => ({
	assetNameExistsErrorMsg: {
		color: theme.palette.alerts.error,
	},
	textField: { width: '98%' },
	success: { fill: theme.palette.alerts.success },
	error: { fill: theme.palette.alerts.error },
	statusMsg: {
		display: 'flex',
	},
}));

// Re-usable asset name text field (upload files page and add details page).
// including asset name duplication check both on the UI and backend.
// Enabling forward ref to send ref from parent component.
const AssetNameTextFieldContainer = React.forwardRef((props, ref) => {
	const { asset, index, setAssetEditable } = props;
	const dispatch = useDispatch();
	const [textFieldVal, setTextFieldVal] = React.useState(asset.name);
	const [isAssetNameUniqueInBackEnd, setAssetNameUniqueInBackEndStatus] = React.useState(
		asset.isAssetNameUniqueInBackEnd,
	);
	const [assetUUID, setAssetUUID] = React.useState(asset.existingAssetUUID);
	let isBuyable = true;
	if (asset.product && asset.product.product) {
		isBuyable = asset.product.product.isBuyable === 'Y';
	}
	const uiAssets = useSelector((state) => state.assets);
	const [isLoading, setLoading] = React.useState(false);
	const isAssetNameAvailable = asset.isAssetNameUnqiueOnUI && asset.isAssetNameUniqueInBackEnd;
	const displayErrorStatus = !isAssetNameAvailable && asset.name.length > 0;
	const isAssetArchived = asset.archived;
	const classes = useStyles();

	// searching for duplicates in the assets currently being created on the UI (which are not been submitted)
	// and in the backend by calling API.
	const SearchForAsset = (txtBoxVal, assets, asset) => {
		// searching for Ui assets
		if (assets.some((a) => a.name === txtBoxVal && a.isAssetNameUnqiueOnUI)) {
			setLoading(false);
			setAssetNameUniqueInBackEndStatus(true);
			const assetObj = { ...asset, isAssetNameUnqiueOnUI: false, name: txtBoxVal };
			dispatch(assetsActions.updateAssetsData(assetObj));
		} else {
			dispatch(searchActions.loadSearchData(txtBoxVal, searchConstants.assets3dVal))
				.then(function (response) {
					// checking if the asset exists in the back end
					const backEndAsset = response.find((r) => r.name === txtBoxVal && !r.archived);
					if (backEndAsset) {
						setAssetNameUniqueInBackEndStatus(false);
						setAssetUUID(backEndAsset.id);
						const assetObj = {
							...asset,
							isAssetNameUnqiueOnUI: true,
							isAssetNameUniqueInBackEnd: false,
							name: txtBoxVal,
						};
						dispatch(assetsActions.updateAssetsData(assetObj));
					} else {
						setAssetNameUniqueInBackEndStatus(true);
						const assetObj = {
							...asset,
							isAssetNameUnqiueOnUI: true,
							isAssetNameUniqueInBackEnd: true,
							name: txtBoxVal,
						};
						dispatch(assetsActions.updateAssetsData(assetObj));
					}
					setLoading(false);
				})
				.catch((error) => {
					console.log(error);
				});
		}
	};
	// implementing debounce for asset name search.
	// only trigger API call when there is a delay of mentioned time from last keystroke
	const DebounceAssetNameSearch = useCallback(
		debounce(apiCallDebounceTime, false, (a, assets, asset) => SearchForAsset(a, assets, asset)),
		[],
	);

	const handleAssetTextFieldOnChange = (event) => {
		const txtBoxVal = event.target.value;
		if (txtBoxVal.length > 0) {
			setLoading(true);
			// calling debounce function
			DebounceAssetNameSearch(txtBoxVal, uiAssets, asset);
		} else {
			setLoading(false);
			const assetObj = {
				...asset,
				name: txtBoxVal,
				isAssetNameUnqiueOnUI: true,
				isAssetNameUniqueInBackEnd: true,
			};
			dispatch(assetsActions.updateAssetsData(assetObj));
		}
		setTextFieldVal(txtBoxVal);
	};
	// on blur disable the edit mode
	const handleBlur = () => {
		// setting editable state to false for (add details page, asset state )
		// only assets text box with valid name should be closed when on blur.
		if (setAssetEditable !== undefined && asset.isAssetNameUnqiueOnUI && asset.isAssetNameUniqueInBackEnd) {
			setAssetEditable(false);
		}
	};
	const RenderStatus = ({ icon, label = '' }) => {
		return (
			<Grid container spacing={2}>
				<Grid item className={classes.statusMsg}>
					{icon}
					<Box m={0.3} />
					<Typography variant="body1">{label}</Typography>
				</Grid>
			</Grid>
		);
	};
	// Component to render asset name status (loading icon when typing, Check Icon when asset name is unique, error icon when asset name is not unique)
	const RenderStatusIcons = () => {
		if (isLoading) {
			return <RenderStatus icon={<CircularProgress size={24} />} label={checkingForAsstTxt} />;
		} else if (isAssetArchived) {
			return <ArchiveIcon className={classes.error} data-cy="archived-icon" />;
		} else if (isAssetNameAvailable && textFieldVal.length > 0) {
			return (
				<RenderStatus icon={<CheckCircleSharpIcon className={classes.success} data-cy="check-circle-icon" />} />
			);
		} else if (displayErrorStatus) {
			return <ErrorIcon className={classes.error} data-cy="error-icon" />;
		} else {
			return null;
		}
	};
	const RenderAssetNameErrorMsg = () => {
		const AssetExitsWithLink = ({ pre }) => {
			return (
				<RenderStatus
					label={
						<span>
							{pre}
							<ManageAssetInNewTabLink
								assetUUID={assetUUID}
								linkTxt={clickHereTxt}
							></ManageAssetInNewTabLink>
							{postAssetExistMsg}
						</span>
					}
				/>
			);
		};
		if (displayErrorStatus) {
			if (isAssetArchived) {
				return <AssetExitsWithLink pre={assetExistAndArchived} />;
			} else if (!isBuyable) {
				return <RenderStatus label="Asset is not buyable." />;
			} else if (!isAssetNameUniqueInBackEnd) {
				return <AssetExitsWithLink pre={preAssetExistMsg} />;
			} else {
				return <RenderStatus label={preAssetExistMsg} />;
			}
		} else {
			return null;
		}
	};

	return (
		<Grid container item alignItems="center" xs={12}>
			<Grid item xs={6}>
				<Tooltip title={assetNameTxtBoxTooltip} arrow={true} placement="top">
					<TextField
						className={classes.textField}
						id="asset-name"
						variant="outlined"
						data-cy={'asset-textbox-' + index}
						size="small"
						ref={ref}
						type="search"
						value={textFieldVal}
						onChange={handleAssetTextFieldOnChange}
						placeholder={'New Asset ' + index}
						error={displayErrorStatus}
						onBlur={handleBlur}
						autoComplete="off"
					/>
				</Tooltip>
			</Grid>
			<Grid item xs={6} className={classes.statusMsg}>
				{/* loading spinner and text indicating API call in progress */}
				{RenderStatusIcons()}
				{/* displaying error status basing on UI asset exists vs backend */}
				{RenderAssetNameErrorMsg()}
			</Grid>
		</Grid>
	);
});

export default AssetNameTextFieldContainer;
